import { useEffect, useState } from 'react';
import { Modal, Drawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useJunoCommonFunctions } from '../../../client/Juno/helpers';
import { defaultCurrenciesToDisplay } from '../../helpers/constant';
import useWindowWidth from '../../hooks/useWindowWidth';
import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';

const MOBILE_BREAKPOINT = 600;

export default function ManageCurrenciesModal({ open, onClose, balancesData = [] }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;

  const imageBaseUrl = useSelector(s => s.config.image_base_url);
  const { clientData, managedCurrencies } = useSelector(s => s.juno);
  const { updateManagedCurrencies } = useJunoCommonFunctions();

  const [selected, setSelected] = useState(defaultCurrenciesToDisplay);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    const initialSelection = managedCurrencies?.length
      ? managedCurrencies
      : defaultCurrenciesToDisplay;
    setSelected(initialSelection);
    setAlertMsg('');
  }, [managedCurrencies, open]);

  const toggle = code => {
    setSelected(prev => {
      const next = prev.includes(code)
        ? prev.filter(val => val !== code)
        : [...prev, code];

      if (!next.length) {
        setAlertMsg('At least one currency must be selected');
        return prev;
      }
      setAlertMsg('');
      return next;
    });
  };

  const save = () => {
    if (clientData?.clientId && selected.length > 0) {
      updateManagedCurrencies(clientData.clientId, selected);
    }
    onClose();
  };

  const items = balancesData.map(({ currencyShortName, currencyIcon }) => ({
    code: currencyShortName,
    icon: currencyIcon,
    active: selected.includes(currencyShortName),
  }));

  const Card = () => (
    <div className="!bg-modal-bg rounded-lg max-w-xl w-full p-6 flex flex-col gap-6 h-screen sm:h-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="xxs:!px-0 font-semibold !text-[20px] uppercase p-0 rounded-lg dark:!text-text items-center">
          Manage Currencies
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <CloseIcon  className='!text-text'/>
        </button>
      </div>

      {/* Instruction */}
      <p className="text-[14px] font-medium text-balanceCard-manage uppercase leading-[1.5] w-full">
        Select or deselect one or multiple balance below you would<br></br> like to see
        within dashboard.
      </p>

      {/* Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 gap-2">
        {items.map(({ code, icon, active }) => (
          <div
            key={code}
            onClick={() => toggle(code)}
            className={`flex justify-center items-center flex-col p-[10px] rounded-lg 
                 border cursor-pointer relative bg-[#F4F4F5] dark:bg-secondary hover:bg-[#E4E4E7] 
                  dark:hover:!bg-[#70707B] dark:hover:border-[#70707B] 
                  dark:border-input-border ${active
                ? 'is-active !border-primary dark:!border-[#FFFFFF]'
                : '!opacity-[0.7] !border-gray-custom dark:!border-input-border'
              }`}>
            {active && (
              <CheckIcon className="absolute !text-[16px] !top-[3px] !right-[5px] dark:!text-text !h-[18px] !w-[18px]" />
            )}
            <img
              src={`${imageBaseUrl}${icon}`}
              alt={code}
              className="w-10 h-10 mb-2 rounded-full"
            />
            <span className="text-sm font-medium dark:!text-text">{code}</span>
          </div>
        ))}
      </div>

      {alertMsg && (
        <div className={`alert alert-danger`} role="alert">
          {alertMsg}
        </div>
      )}

      {/* Footer */}
      <button
        onClick={save}
        className="w-full p-3 !rounded-lg !text-white dark:!text-[#18181B]  uppercase font-semibold !bg-primary font-inter !text-sm text-center xxs:!mt-auto"
      >
        Apply
      </button>
    </div>
  );

  return isMobile ? (
    <Drawer anchor="bottom" open={open} onClose={onClose}>
      <div className="!bg-modal-bg">
        <Card />
      </div>
    </Drawer>
  ) : (
    <Modal open={open} onClose={onClose}>
      {/* here: center the Card */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <Card />
      </div>
    </Modal>
  );
}
